import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  MapPin,
  Clock,
  User,
  UserCog,
  Play,
  CheckCircle,
  Calendar as CalendarIcon,
  GripVertical,
  Navigation,
  Plus,
  MoveHorizontal,
  AlertTriangle,
  Users,
  Lock,
  XCircle,
} from 'lucide-react';
import { cn, localToday } from '@/lib/utils';
import { AddOrderDialog } from '@/pages/OrdersPage';
import { toast } from 'sonner';

// ─── Constants ──────────────────────────────────────────────
const STATUS_COLORS = {
  new: 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600',
  confirmed: 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700',
  in_progress: 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700',
  done: 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700',
  cancelled: 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700',
};
const WEEKDAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const WEEKDAY_SHORT_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

function getMonday(d) {
  const date = new Date(String(d).slice(0, 10) + 'T12:00:00');
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
}

function formatDateShort(dateStr, lang) {
  // Parse as local noon to avoid UTC-midnight timezone display issues
  const d = new Date(String(dateStr).slice(0, 10) + 'T12:00:00');
  return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', { day: 'numeric', month: 'short' });
}

function getWorkloadConfig(loadPercent, t) {
  if (loadPercent === 0) return { label: t('common.free'), color: 'bg-slate-100 text-slate-500 dark:bg-slate-800', bar: 'bg-slate-300 dark:bg-slate-600', pct: 0 };
  if (loadPercent <= 40) return { label: t('calendar.workloadLow'), color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', bar: 'bg-green-500', pct: loadPercent };
  if (loadPercent <= 65) return { label: t('calendar.workloadMedium'), color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', bar: 'bg-amber-500', pct: loadPercent };
  return { label: t('calendar.workloadHigh'), color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', bar: 'bg-red-500', pct: Math.min(loadPercent, 100) };
}

function getDayWorkloadConfig(count, t) {
  if (count === 0) return { color: 'bg-slate-100 text-slate-500 dark:bg-slate-800' };
  if (count <= 2) return { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
  if (count <= 4) return { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
  return { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
}

// ─── EventCard ──────────────────────────────────────────────
function EventCard({ event, onClick, onDragStart }) {
  return (
    <button
      onClick={() => onClick(event)}
      draggable
      onDragStart={(e) => onDragStart?.(e, event)}
      className={cn(
        "w-full text-left p-3 rounded-lg border-l-4 transition-all hover:shadow-md",
        STATUS_COLORS[event.operational_status],
      )}
      style={{ borderLeftColor: event.service_color }}
      data-testid={`event-${event.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{event.title}</p>
          <p className="text-xs text-muted-foreground truncate">{event.client_name}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {event.time_start && (
            <Badge variant="outline" className="text-xs">{event.time_start}</Badge>
          )}
          <GripVertical className="h-4 w-4 text-muted-foreground/40 hidden md:block" />
        </div>
      </div>
      {event.assigned_workers?.length > 0 && (
        <div className="flex items-center gap-1 mt-1.5">
          {event.assigned_workers.slice(0, 3).map(w => (
            <div key={w.id} className="w-5 h-5 rounded-full text-white text-[10px] flex items-center justify-center"
              style={{ backgroundColor: w.color }} title={w.name}>
              {w.name?.split(' ').map(n => n[0]).join('')}
            </div>
          ))}
        </div>
      )}
    </button>
  );
}

// ─── EventDetailSheet ───────────────────────────────────────
function EventDetailSheet({ event, open, onOpenChange, onStatusChange, onReassign, workers, isManager, t }) {
  const navigate = useNavigate();
  const [showReassign, setShowReassign] = useState(false);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  
  useEffect(() => {
    if (event?.assigned_workers) {
      setSelectedWorkers(event.assigned_workers.map(w => w.id));
    }
  }, [event]);
  
  const toggleWorker = (workerId) => {
    setSelectedWorkers(prev => 
      prev.includes(workerId) ? prev.filter(id => id !== workerId) : [...prev, workerId]
    );
  };
  
  const handleReassign = () => {
    if (onReassign && event) {
      onReassign(event.order_id, selectedWorkers);
      setShowReassign(false);
    }
  };
  
  if (!event) return null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl">
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: event.service_color }} />
            <SheetTitle className="truncate">{event.title}</SheetTitle>
          </div>
          <SheetDescription asChild>
            <div className="flex items-center gap-2">
              <Badge className="text-xs">{t(`status.${event.operational_status}`)}</Badge>
              {event.service_type_name && <span className="text-xs">{event.service_type_name}</span>}
            </div>
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4 overflow-y-auto pb-safe">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <User className="h-5 w-5 text-muted-foreground" />
            <div><p className="font-medium">{event.client_name}</p><p className="text-xs text-muted-foreground">{t('orders.client')}</p></div>
          </div>
          {event.time_start && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <div><p className="font-medium">{event.time_start} — {event.time_end || '?'}</p><p className="text-xs text-muted-foreground">{event.duration_minutes} min</p></div>
            </div>
          )}
          {event.address && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <MapPin className="h-5 w-5 text-muted-foreground" />
              <p className="font-medium flex-1">{event.address}</p>
              <Button size="sm" variant="outline"
                onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(event.address)}`, '_blank')}
                data-testid="calendar-navigate">
                <Navigation className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {/* Workers section */}
          <div className="p-3 rounded-lg bg-muted/50">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-muted-foreground">{t('orders.assignedTo')}</p>
              {isManager && (
                <Button variant="ghost" size="sm" className="h-7 text-xs"
                  onClick={() => setShowReassign(!showReassign)} data-testid="reassign-btn">
                  <UserCog className="h-3.5 w-3.5 mr-1" /> {t('calendar.reassign')}
                </Button>
              )}
            </div>
            {showReassign ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {workers.filter(w => w.is_active).map(w => {
                    const isSelected = selectedWorkers.includes(w.id);
                    return (
                      <button key={w.id} onClick={() => toggleWorker(w.id)}
                        className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all",
                          isSelected ? "bg-accent text-white" : "bg-background hover:bg-muted border")}
                        data-testid={`worker-select-${w.id}`}>
                        <div className="w-4 h-4 rounded-full border-2" 
                          style={{ backgroundColor: isSelected ? w.color || '#3b82f6' : 'transparent', borderColor: w.color || '#3b82f6' }} />
                        <span>{w.first_name} {w.last_name}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleReassign} disabled={selectedWorkers.length === 0} data-testid="confirm-reassign">
                    {t('common.save')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowReassign(false); setSelectedWorkers(event.assigned_workers?.map(w => w.id) || []); }}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {event.assigned_workers?.length > 0 ? event.assigned_workers.map(w => (
                  <div key={w.id} className="flex items-center gap-2 px-2 py-1 rounded-full bg-background">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: w.color }} />
                    <span className="text-sm">{w.name}</span>
                  </div>
                )) : <p className="text-sm text-muted-foreground">{t('calendar.noWorkerAssigned')}</p>}
              </div>
            )}
          </div>
          
          <div className="flex flex-col gap-2 pt-4">
            {isManager ? (
              /* ── Менеджер: управление заданием, НЕ выполнением ── */
              <>
                {event.operational_status !== 'done' && event.operational_status !== 'cancelled' && (
                  <Button
                    variant="outline"
                    className="w-full h-12"
                    onClick={() => setShowReassign(!showReassign)}
                  >
                    <UserCog className="mr-2 h-5 w-5" /> Переназначить
                  </Button>
                )}
                {event.operational_status !== 'done' && event.operational_status !== 'cancelled' && (
                  <Button
                    variant="destructive"
                    className="w-full h-12"
                    onClick={() => { onStatusChange(event.order_id, 'cancelled'); onOpenChange(false); }}
                  >
                    <XCircle className="mr-2 h-5 w-5" /> Отменить заказ
                  </Button>
                )}
                {event.operational_status === 'done' && (
                  <div className="w-full h-12 flex items-center justify-center rounded-lg bg-green-500/10 border border-green-500/30 text-green-600 font-medium text-sm gap-2">
                    <CheckCircle className="h-5 w-5" /> Выполнен — ожидает оплаты
                  </div>
                )}
              </>
            ) : (
              /* ── Воркер: управление процессом работы ── */
              <>
                {event.operational_status === 'confirmed' && (
                  <Button className="w-full h-12" onClick={() => onStatusChange(event.order_id, 'in_progress')}>
                    <Play className="mr-2 h-5 w-5" /> Начать работу
                  </Button>
                )}
                {event.operational_status === 'in_progress' && (
                  <Button className="w-full h-12 bg-green-600 hover:bg-green-700" onClick={() => onStatusChange(event.order_id, 'done')}>
                    <CheckCircle className="mr-2 h-5 w-5" /> Завершить работу
                  </Button>
                )}
                {event.operational_status === 'new' && (
                  <div className="w-full h-12 flex items-center justify-center rounded-lg bg-muted text-muted-foreground text-sm">
                    Ожидает подтверждения менеджером
                  </div>
                )}
              </>
            )}
            <Button variant="ghost" className="w-full h-10 text-sm" onClick={() => { onOpenChange(false); navigate(`/orders?view=${event.order_id}`); }}>
              {t('action.openOrder')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── DayView ────────────────────────────────────────────────
function DayView({ events, softLocks, onEventClick, onDrop, onAddClick, canAdd, t }) {
  const timeSlots = [];
  for (let hour = 7; hour <= 21; hour++) {
    const timeStr = `${hour.toString().padStart(2, '0')}:00`;
    const slotEvents = events.filter(e => e.time_start && parseInt(e.time_start.split(':')[0]) === hour);
    const slotLocks = (softLocks || []).filter(l => l.time_start && parseInt(l.time_start.split(':')[0]) === hour);
    timeSlots.push({ time: timeStr, hour, events: slotEvents, locks: slotLocks });
  }
  const unscheduled = events.filter(e => !e.time_start);
  const handleDragOver = (e) => { e.preventDefault(); e.currentTarget.classList.add('bg-accent/10'); };
  const handleDragLeave = (e) => { e.currentTarget.classList.remove('bg-accent/10'); };
  const handleDrop = (e, hour) => {
    e.preventDefault(); e.currentTarget.classList.remove('bg-accent/10');
    const eventId = e.dataTransfer.getData('eventId');
    if (eventId && onDrop) onDrop(eventId, `${hour.toString().padStart(2, '0')}:00`);
  };
  return (
    <div className="space-y-1">
      {canAdd && (
        <div className="flex justify-end mb-4">
          <Button onClick={() => onAddClick()} size="sm" className="gap-2" data-testid="add-order-from-calendar">
            <Plus className="h-4 w-4" /> {t('calendar.addOrder')}
          </Button>
        </div>
      )}
      {unscheduled.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-muted-foreground mb-2">{t('calendar.unscheduled')}</p>
          <div className="space-y-2">{unscheduled.map(ev => <EventCard key={ev.id} event={ev} onClick={onEventClick} />)}</div>
        </div>
      )}
      {timeSlots.map(slot => (
        <div key={slot.time} className="group flex gap-3 transition-colors rounded-lg hover:bg-muted/30"
          onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, slot.hour)}>
          <div className="w-12 text-xs text-muted-foreground pt-3 shrink-0">{slot.time}</div>
          <div className="flex-1 min-h-[52px] border-t border-dashed border-border pt-2 relative">
            {slot.events.length > 0 || slot.locks.length > 0 ? (
              <div className="space-y-2">
                {slot.events.map(ev => (
                  <EventCard key={ev.id} event={ev} onClick={onEventClick}
                    onDragStart={(e, event) => { e.dataTransfer.setData('eventId', event.order_id); }} />
                ))}
                {slot.locks.map(lk => (
                  <div key={lk.id}
                    className="w-full p-3 rounded-lg border-l-4 border-dashed border-amber-400/60 bg-amber-500/10 opacity-70"
                    data-testid={`soft-lock-${lk.id}`}>
                    <div className="flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5 text-amber-500" />
                      <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                        Soft Lock: {lk.time_start} - {lk.time_end}
                      </span>
                      {lk.worker_name && <span className="text-[10px] text-muted-foreground ml-auto">{lk.worker_name}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : canAdd && (
              <button onClick={() => onAddClick(slot.time)}
                className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`add-order-slot-${slot.hour}`}>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-accent">
                  <Plus className="h-3.5 w-3.5" /> <span>{t('calendar.clickToAdd')}</span>
                </div>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── WeekView (worker's week) ───────────────────────────────
function WeekView({ weekData, selectedDate, onDayClick, onDropToDay, canDrag, t, lang }) {
  const today = localToday();
  const [dragOverDate, setDragOverDate] = useState(null);
  const [draggingOrder, setDraggingOrder] = useState(null);
  const [draggingOrderTime, setDraggingOrderTime] = useState(null);
  
  const checkTimeConflict = (targetDate, orderTimeStart, orderTimeEnd) => {
    if (!orderTimeStart) return { hasConflict: false };
    const targetDay = weekData.find(d => d.date === targetDate);
    if (!targetDay || targetDay.events.length === 0) return { hasConflict: false };
    const parseTime = (timeStr) => { if (!timeStr) return null; const [h, m] = timeStr.split(':').map(Number); return h * 60 + (m || 0); };
    const dragStart = parseTime(orderTimeStart);
    const dragEnd = parseTime(orderTimeEnd) || (dragStart + 60);
    for (const event of targetDay.events) {
      const eventStart = parseTime(event.time_start);
      const eventEnd = parseTime(event.time_end) || (eventStart + 60);
      if (eventStart === null) continue;
      if (dragStart < eventEnd && dragEnd > eventStart) return { hasConflict: true, conflictWith: event.title, conflictTime: event.time_start };
    }
    return { hasConflict: false };
  };
  
  const handleDragOver = (e, date) => { e.preventDefault(); setDragOverDate(date); };
  const handleDragLeave = () => { setDragOverDate(null); };
  const handleDrop = (e, date) => {
    e.preventDefault();
    const conflict = draggingOrderTime ? checkTimeConflict(date, draggingOrderTime.start, draggingOrderTime.end) : { hasConflict: false };
    if (conflict.hasConflict) {
      toast.error(t('calendar.dropBlocked'), { description: `${t('calendar.timeConflict')}: ${conflict.conflictTime} — ${conflict.conflictWith}`, icon: <AlertTriangle className="h-4 w-4" /> });
      setDragOverDate(null); setDraggingOrder(null); setDraggingOrderTime(null);
      return;
    }
    setDragOverDate(null); setDraggingOrder(null); setDraggingOrderTime(null);
    const orderId = e.dataTransfer.getData('orderId');
    if (orderId && onDropToDay) onDropToDay(orderId, date);
  };
  
  const hasEvents = weekData.some(d => d.events.length > 0);
  const conflictInfo = dragOverDate && draggingOrderTime ? checkTimeConflict(dragOverDate, draggingOrderTime.start, draggingOrderTime.end) : { hasConflict: false };
  
  return (
    <div className="space-y-2">
      {canDrag && hasEvents && !draggingOrder && (
        <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground bg-muted/30 rounded-lg mb-2">
          <GripVertical className="h-3.5 w-3.5" /> <span>{t('calendar.dragHint')}</span>
        </div>
      )}
      {draggingOrder && (
        <div className={cn("flex items-center gap-2 px-3 py-2 text-xs rounded-lg mb-2 transition-colors",
          conflictInfo.hasConflict ? "text-red-600 bg-red-100 dark:bg-red-900/30 border border-red-300" : "text-accent bg-accent/10 animate-pulse")}>
          {conflictInfo.hasConflict ? (
            <><AlertTriangle className="h-3.5 w-3.5" /><span>{t('calendar.timeConflict')}: {conflictInfo.conflictTime} — {conflictInfo.conflictWith}</span></>
          ) : (
            <><MoveHorizontal className="h-3.5 w-3.5" /><span>{draggingOrder}</span></>
          )}
        </div>
      )}
      {weekData.map((day) => {
        const isToday = day.date === today;
        const isSelected = day.date === selectedDate;
        const isDragOver = dragOverDate === day.date;
        const d = new Date(day.date + 'T12:00:00');
        const weekdayIdx = (d.getDay() + 6) % 7;
        const dayConflict = isDragOver && draggingOrderTime ? checkTimeConflict(day.date, draggingOrderTime.start, draggingOrderTime.end) : { hasConflict: false };
        
        return (
          <div key={day.date}
            onDragOver={(e) => canDrag && handleDragOver(e, day.date)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => canDrag && handleDrop(e, day.date)}
            className={cn(
              "w-full text-left p-4 rounded-xl border transition-all",
              isSelected && "border-accent bg-accent/5 shadow-sm",
              isToday && !isSelected && "border-accent/50 bg-accent/5",
              !isToday && !isSelected && "border-border hover:border-accent/30",
              isDragOver && !dayConflict.hasConflict && "border-accent border-dashed bg-accent/10 scale-[1.01]",
              isDragOver && dayConflict.hasConflict && "border-red-500 border-dashed bg-red-50 dark:bg-red-900/20 scale-[1.01]"
            )}
            data-testid={`week-day-${day.date}`}>
            <button onClick={() => onDayClick(day.date)} className="w-full text-left">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn("w-10 h-10 rounded-lg flex flex-col items-center justify-center text-xs font-medium",
                    isToday ? "bg-accent text-white" : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400")}>
                    <span className="text-[10px] uppercase">{t(`weekday.${WEEKDAY_SHORT_KEYS[weekdayIdx]}`)}</span>
                    <span className="text-sm font-bold">{d.getDate()}</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      {t(`weekday.${WEEKDAY_KEYS[weekdayIdx]}`)}
                      {isToday && <span className="text-accent ml-2 text-xs">{t('common.today')}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatDateShort(day.date, lang)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {day.events.length > 0 ? (
                    <Badge variant={isToday ? "default" : "secondary"} className="text-xs">{t('calendar.orders', day.events.length)}</Badge>
                  ) : <span className="text-xs text-muted-foreground">{t('common.free')}</span>}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </button>
            {day.events.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {day.events.slice(0, 3).map(ev => (
                  <div key={ev.id} draggable={canDrag}
                    onDragStart={(e) => { e.dataTransfer.setData('orderId', ev.order_id); setDraggingOrder(ev.title); setDraggingOrderTime({ start: ev.time_start, end: ev.time_end }); e.stopPropagation(); }}
                    onDragEnd={() => { setDraggingOrder(null); setDraggingOrderTime(null); }}
                    className={cn("flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted text-xs transition-all",
                      canDrag && "cursor-grab hover:bg-muted/80 hover:scale-105 active:cursor-grabbing active:opacity-50")}>
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ev.service_color }} />
                    <span className="truncate max-w-[120px]">{ev.time_start || '?'} {ev.title}</span>
                    {canDrag && <GripVertical className="h-3 w-3 text-muted-foreground/50 ml-1" />}
                  </div>
                ))}
                {day.events.length > 3 && <span className="text-xs text-muted-foreground px-2 py-1">+{day.events.length - 3}</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── TeamGrid (NEW default view) ────────────────────────────
function TeamGrid({ workers, weekDays, scheduleData, softLocks, today, onCellClick, onWorkerClick, workerLoadMap, t, lang }) {
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';

  // Build a map: workerId -> date -> locks[]
  const lockMap = {};
  for (const lk of (softLocks || [])) {
    const key = `${lk.worker_id}_${lk.date}`;
    if (!lockMap[key]) lockMap[key] = [];
    lockMap[key].push(lk);
  }
  
  return (
    <div className="space-y-3">
      {workers.map((worker) => {
        let weeklyTotal = 0;
        for (const day of weekDays) weeklyTotal += (scheduleData[worker.id]?.[day]?.length || 0);
        const loadPct = workerLoadMap?.[worker.id] ?? 0;
        const workload = getWorkloadConfig(loadPct, t);

        return (
          <Card key={worker.id} className="cgc-card overflow-hidden" data-testid={`team-worker-${worker.id}`}>
            <CardHeader className="pb-2 pt-3 px-4 cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => onWorkerClick(worker)} data-testid={`team-worker-header-${worker.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold"
                    style={{ backgroundColor: worker.calendar_color || worker.color || '#3b82f6' }}>
                    {worker.first_name?.[0]}{worker.last_name?.[0]}
                  </div>
                  <div>
                    <CardTitle className="text-sm">{worker.first_name} {worker.last_name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{worker.specialization || t('role.worker')}</p>
                  </div>
                </div>
                <Badge className={cn("text-[10px]", workload.color)}>
                  {Math.round(loadPct)}%
                </Badge>
              </div>
              <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className={cn("h-full rounded-full transition-all", workload.bar)}
                  style={{ width: `${Math.max(workload.pct, loadPct > 0 ? 5 : 0)}%` }} />
              </div>
            </CardHeader>

            <CardContent className="px-3 pb-3 pt-0">
              <div className="grid grid-cols-7 gap-1">
                {weekDays.map((date, idx) => {
                  const events = scheduleData[worker.id]?.[date] || [];
                  const locks = lockMap[`${worker.id}_${date}`] || [];
                  const isToday = date === today;
                  const dayConf = getDayWorkloadConfig(events.length, t);
                  const hasLocks = locks.length > 0;

                  return (
                    <button key={date}
                      onClick={() => onCellClick(worker, date)}
                      className={cn(
                        "rounded-lg p-1.5 text-center min-h-[56px] border transition-all cursor-pointer hover:border-accent/70 hover:shadow-sm",
                        isToday ? "border-emerald-500 bg-emerald-500/10" : "border-accent/40 bg-accent/5",
                        hasLocks && !isToday && "border-dashed border-amber-400/60 bg-amber-500/5"
                      )}
                      data-testid={`team-cell-${worker.id}-${date}`}>
                      <p className={cn("text-[10px] font-medium uppercase", isToday ? "text-emerald-400" : "text-muted-foreground")}>
                        {t(`weekday.${WEEKDAY_SHORT_KEYS[idx]}`)}
                      </p>
                      <p className={cn("text-xs font-bold", isToday && "text-emerald-400")}>
                        {new Date(date + 'T12:00:00').getDate()}
                      </p>
                      {events.length > 0 || hasLocks ? (
                        <div className="mt-1 flex items-center justify-center gap-0.5">
                          {events.length > 0 && (
                            <span className={cn("inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-full", dayConf.color)}>
                              {events.length}
                            </span>
                          )}
                          {hasLocks && (
                            <span className="inline-flex items-center text-[9px] text-amber-500" title={`${locks.length} soft lock(s)`}>
                              <Lock className="h-2.5 w-2.5" />
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-[9px] text-muted-foreground mt-1.5">—</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Main CalendarPage ──────────────────────────────────────
export default function CalendarPage() {
  const { api, user, worker } = useAuth();
  const { t, lang } = useLanguage();
  const isManager = user?.role === 'admin' || user?.role === 'manager';
  // Views: 'team' (grid), 'week' (worker's week), 'day' (worker's day)
  // Workers default to 'week' view showing only their schedule
  const [view, setView] = useState(isManager ? 'team' : 'week');
  const [currentDate, setCurrentDate] = useState(localToday());
  const [weekData, setWeekData] = useState([]);
  const [dayEvents, setDayEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [preselectedTime, setPreselectedTime] = useState('');
  // Team grid data
  const [teamSchedule, setTeamSchedule] = useState({});
  // Soft locks for calendar visualization
  const [softLocks, setSoftLocks] = useState([]);
  // Worker load data (from /workers/load)
  const [workerLoadMap, setWorkerLoadMap] = useState({});
  
  const monday = getMonday(currentDate);
  const mondayStr = localToday(monday);
  const weekEnd = new Date(monday); weekEnd.setDate(weekEnd.getDate() + 6);
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  const today = localToday();

  // Week days for team grid
  const teamWeekDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    teamWeekDays.push(localToday(d));
  }

  // Fetch workers once; for worker role, auto-select self
  useEffect(() => {
    api.get('/workers').then(res => {
      const active = res.data.filter(w => w.is_active !== false);
      setWorkers(active);
      // If worker role, auto-select their own worker record
      if (!isManager && worker?.id) {
        const me = active.find(w => w.id === worker.id);
        if (me) setSelectedWorker(me);
      }
    }).catch(() => {});
  }, [api, isManager, worker?.id]);

  // Fetch team schedule for team view
  const fetchTeamSchedule = useCallback(async () => {
    setLoading(true);
    const weekEndStr = localToday(new Date(new Date(mondayStr + 'T12:00:00').getTime() + 6 * 86400000));
    try {
      const [weekRes, locksRes, loadRes] = await Promise.all([
        api.get(`/calendar/week?week_start=${mondayStr}`),
        api.get(`/calendar/soft-locks?week_start=${mondayStr}`),
        api.get(`/workers/load?week_start=${mondayStr}&week_end=${weekEndStr}`),
      ]);
      const sched = {};
      for (const w of workers) {
        sched[w.id] = {};
        for (const day of teamWeekDays) sched[w.id][day] = [];
      }
      if (weekRes.data.days) {
        for (const day of weekRes.data.days) {
          for (const ev of day.events) {
            const workerIds = ev.assigned_workers?.map(aw => aw.id) || [];
            for (const wid of workerIds) {
              if (sched[wid] && sched[wid][day.date]) sched[wid][day.date].push(ev);
            }
          }
        }
      }
      setTeamSchedule(sched);
      setSoftLocks(locksRes.data?.locks || []);
      // Build load map: worker_id -> weekly_load_percent
      const lMap = {};
      for (const w of (loadRes.data?.workers || [])) {
        lMap[w.worker_id] = w.weekly_load_percent || 0;
      }
      setWorkerLoadMap(lMap);
    } catch (err) { console.error('Failed to fetch team schedule:', err); }
    finally { setLoading(false); }
  }, [api, mondayStr, workers]);

  // Fetch worker week
  const fetchWeek = useCallback(async (weekStart) => {
    if (!selectedWorker) return;
    setLoading(true);
    try {
      const response = await api.get(`/calendar/week?week_start=${weekStart}&worker_ids=${selectedWorker.id}`);
      setWeekData(response.data.days);
    } catch (err) { console.error('Failed to fetch week:', err); }
    finally { setLoading(false); }
  }, [api, selectedWorker]);

  // Fetch worker day
  const fetchDay = useCallback(async (date) => {
    if (!selectedWorker) return;
    setLoading(true);
    try {
      const [dayRes, locksRes] = await Promise.all([
        api.get(`/calendar/day?date=${date}&worker_ids=${selectedWorker.id}`),
        api.get(`/calendar/soft-locks?week_start=${date}&week_end=${date}`),
      ]);
      setDayEvents(dayRes.data.events);
      // Filter locks to selected worker only
      const workerLocks = (locksRes.data?.locks || []).filter(l => l.worker_id === selectedWorker.id);
      setSoftLocks(workerLocks);
    } catch (err) { console.error('Failed to fetch day:', err); }
    finally { setLoading(false); }
  }, [api, selectedWorker]);

  // Data fetching based on view
  useEffect(() => {
    if (view === 'team' && workers.length > 0) fetchTeamSchedule();
    else if (view === 'week' && selectedWorker) fetchWeek(mondayStr);
    else if (view === 'day' && selectedWorker) fetchDay(currentDate);
  }, [view, mondayStr, currentDate, fetchTeamSchedule, fetchWeek, fetchDay, selectedWorker, workers]);

  const navigateWeek = (dir) => {
    const d = new Date(currentDate + 'T12:00:00');
    d.setDate(d.getDate() + (7 * dir));
    setCurrentDate(localToday(d));
  };
  
  const goToToday = () => setCurrentDate(localToday());

  // Team grid cell click → worker week with selected day
  // From team grid: click on specific DAY cell → open that day
  const handleCellClick = (worker, date) => {
    setSelectedWorker(worker);
    setCurrentDate(date);
    setView('day');
  };

  // From team grid: click on worker name/header → open worker's week
  const handleWorkerClick = (worker) => {
    setSelectedWorker(worker);
    setView('week');
  };

  // From week → day
  const handleDayClick = (date) => { setCurrentDate(date); setView('day'); };
  
  // Back navigation
  const handleBackToTeam = () => { setSelectedWorker(null); setView('team'); };
  const handleBackToWeek = () => { setView('week'); };
  
  const handleEventClick = (event) => { setSelectedEvent(event); setDetailOpen(true); };
  const handleAddOrder = (time) => { setPreselectedTime(typeof time === 'string' ? time : ''); setAddOrderOpen(true); };
  
  const handleOrderCreated = () => {
    if (view === 'team') fetchTeamSchedule();
    else if (view === 'week') fetchWeek(mondayStr);
    else fetchDay(currentDate);
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await api.post(`/orders/${orderId}/actions/change-operational-status`, { new_status: newStatus });
      if (view === 'week') fetchWeek(mondayStr); else fetchDay(currentDate);
      setDetailOpen(false);
    } catch (err) {
      console.error('Failed to change status:', err);
      toast.error(t('orders.statusChangeFailed') || 'Ошибка изменения статуса');
    }
  };

  const handleDrop = async (orderId, newTime) => {
    try {
      await api.post(`/orders/${orderId}/reschedule`, { new_date: currentDate, new_time_start: newTime });
      fetchDay(currentDate);
    } catch (err) {
      console.error('Failed to reschedule:', err);
      toast.error(t('calendar.moveError') || 'Ошибка переноса');
    }
  };
  
  const handleDropToDay = async (orderId, newDate) => {
    try {
      const order = weekData.flatMap(d => d.events).find(e => e.order_id === orderId);
      await api.post(`/orders/${orderId}/reschedule`, { new_date: newDate });
      const formattedDate = new Date(newDate + 'T12:00:00').toLocaleDateString(locale, { day: 'numeric', month: 'short' });
      toast.success(t('calendar.orderMoved'), { description: `${order?.title || ''} → ${formattedDate}`, icon: <MoveHorizontal className="h-4 w-4" /> });
      fetchWeek(mondayStr);
    } catch (err) { 
      toast.error(t('calendar.moveError'), { description: err.response?.data?.detail || 'Failed' });
    }
  };
  
  const handleReassign = async (orderId, workerIds) => {
    try {
      await api.post(`/orders/${orderId}/reassign`, { worker_ids: workerIds });
      if (view === 'week') fetchWeek(mondayStr); else fetchDay(currentDate);
      setDetailOpen(false);
      toast.success(t('calendar.reassigned') || 'Работники переназначены');
    } catch (err) {
      console.error('Failed to reassign:', err);
      toast.error(t('calendar.reassignFailed') || 'Ошибка переназначения');
    }
  };

  const weekRangeText = `${monday.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} — ${weekEnd.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  const dayText = new Date(currentDate + 'T12:00:00').toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="space-y-4 pb-20">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          {view === 'team' ? (
            <div>
              <h1 className="text-lg md:text-2xl font-bold flex items-center gap-2" data-testid="calendar-title">
                <Users className="h-6 w-6 text-accent" />
                {t('calendar.teamSchedule')}
              </h1>
              <p className="text-sm text-muted-foreground">{weekRangeText}</p>
            </div>
          ) : view === 'week' ? (
            <div className="flex items-center gap-2">
              {isManager && (
                <Button variant="ghost" size="icon" onClick={handleBackToTeam} data-testid="back-to-team">
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              )}
              <div>
                <h1 className="text-lg md:text-2xl font-bold">
                  {isManager ? (
                    <span className="inline-flex items-center gap-2 px-2 py-0.5 rounded-lg"
                      style={{ backgroundColor: `${selectedWorker?.color || '#4F46E5'}20` }}>
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedWorker?.color || '#4F46E5' }} />
                      {selectedWorker?.first_name} {selectedWorker?.last_name}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CalendarIcon className="h-6 w-6 text-accent" />
                      Моё расписание
                    </span>
                  )}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">{weekRangeText}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handleBackToWeek} data-testid="back-to-week">
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg md:text-2xl font-bold capitalize">{dayText}</h1>
                <p className="text-sm text-muted-foreground">{selectedWorker?.first_name} {selectedWorker?.last_name}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday} data-testid="calendar-today-btn">
              {t('common.today')}
            </Button>
          </div>
        </div>
        
        {/* Week navigation (all views except day) */}
        {view !== 'day' && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigateWeek(-1)} data-testid="prev-week">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigateWeek(1)} data-testid="next-week">
              <ChevronRight className="h-5 w-5" />
            </Button>
            <span className="text-sm text-muted-foreground">{t('common.week')}</span>
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : view === 'team' ? (
        <TeamGrid workers={workers} weekDays={teamWeekDays} scheduleData={teamSchedule}
          softLocks={softLocks} today={today} onCellClick={handleCellClick} onWorkerClick={handleWorkerClick} workerLoadMap={workerLoadMap} t={t} lang={lang} />
      ) : view === 'week' ? (
        <WeekView weekData={weekData} selectedDate={currentDate} onDayClick={handleDayClick}
          onDropToDay={handleDropToDay} canDrag={isManager} t={t} lang={lang} />
      ) : dayEvents.length === 0 ? (
        <Card className="cgc-card">
          <CardContent className="p-4">
            {isManager && (
              <div className="flex justify-end mb-4">
                <Button onClick={() => handleAddOrder()} size="sm" className="gap-2" data-testid="add-order-from-calendar-empty">
                  <Plus className="h-4 w-4" /> {t('calendar.addOrder')}
                </Button>
              </div>
            )}
            <div className="text-center py-12 text-muted-foreground">
              <CalendarIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{t('calendar.noOrders')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="cgc-card">
          <CardContent className="p-4">
            <DayView events={dayEvents} softLocks={softLocks} onEventClick={handleEventClick} onDrop={handleDrop}
              onAddClick={handleAddOrder} canAdd={isManager} t={t} />
          </CardContent>
        </Card>
      )}
      
      <EventDetailSheet event={selectedEvent} open={detailOpen} onOpenChange={setDetailOpen}
        onStatusChange={handleStatusChange} onReassign={handleReassign} workers={workers} isManager={isManager} t={t} />
      
      {isManager && (
        <AddOrderDialog open={addOrderOpen} onOpenChange={setAddOrderOpen} onSuccess={handleOrderCreated}
          preselectedDate={currentDate} preselectedTime={preselectedTime} preselectedWorker={selectedWorker} />
      )}
    </div>
  );
}
